import { parseDecimal } from './numberUtils.js';
import { makeUnique } from './setUtils';
import isNaN from 'lodash/isNaN';

const formatMoney = function (amount, currencyCode, maxDecimalPlaces) {
  // FORMAT A CURRENCY AMOUNT FOR DISPLAY
  // this is a FORMATTER, NOT A PARSER!
  // the amount you pass in should be a Number already, or something that can be coerced to a Number
  // but if a decimal number, the decimal place should use a dot (.) as the decimal separator
  // for parsing of values with commas as decimal separator, use other functions for that... this is for formatting only

  // amount -- should be a Number
  // currencyCode -- should be a 3-digit string like 'USD'
  // maxDecimalPlaces (optional) -- how many decimal places to allow in the formatted value
  //                             -- (default is 4, but can be more or less -- but keep in mind this is just a FORMATTER!)
  //                             -- if not provided, it uses the minor digits for the currency code (from Intl.NumberFormat)

  // RETURNS: null if there is not enough info for a formatted value -- or a STRING - formatted currency amount

  // IMPORTANT - make sure your PARSER has toFixed() with correct number of digits for your backend
  //          - this only DISPLAYS the formatted value... don't let it HIDE invalid values!  (like too many digits)
  // also, do a check when first loading quote sheet to see if users browser supports all this (Intl, etc)
  // see MDN for details:  https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString

  if (!currencyCode) return null; // don't show a price if there's no currency
  // OLD:  return FormatCurrencyHelper(params.value, currencyCode);
  // UPDATES March 3 2022 - to allow more than 2 decimal digits, and force truncation or rounding to 4
  // we will stop using FormatCurrencyHelper -- that does more than we need, and keep it for its own purposes
  // SETTINGS / CONFIG - eventually move these 'settings' things to some other place
  // we are using Number.toLocaleString(locales, options);
  // you can also make an Intl number formatter, then use method on that to format
  // const myFormatter = new Intl.NumberFormat('en-US', opts);
  // const formattedNumber = myFormatter.format(number)
  const locale = undefined; // can be 'en-US' or something else... using undefined will use host default (should)
  const formatOptions = {
    style: 'currency', // options are 'currency', 'decimal', 'percent', 'unit'
    currency: currencyCode, // 3 letter currency code
    currencyDisplay: 'symbol', // 'symbol', 'narrowSymbol', 'code', 'name' -- see docs -- default is 'symbol'
    currencySign: 'standard', // 'accounting' (parenths) or 'standard' (-) -- default is 'standard'
    // lots more options... signDisplay, notation,
    // roundingMode -- many options... we should not use this... should use toFixed() in parser instead!
    minimumIntegerDigits: 1, // default is 1... always want a leading 0 for decimals
    // minimumFractionDigits: 2,  // USE DEFAULTS - the default for currency formatting is the number of minor unit digits provided by the ISO 4217 currency code list (2 if the list doesn't provide that information).
    // for maximumFractionDigits we want to specifically allow up to 4 (or more if you increase backend digits)
    // ONLY ADD THIS IF SPECIFIED: maximumFractionDigits: maxDecimalPlaces,  // the default for currency formatting is the larger of minimumFractionDigits and the number of minor unit digits provided by the ISO 4217 currency code list (2 if the list doesn't provide that information)
  };
  if (maxDecimalPlaces !== null && maxDecimalPlaces !== undefined) {
    // if we have a maxDecimalPlaces, then we will set that as the maximumFractionDigits
    // this is for formatting, not parsing, so we can use this to limit the number of digits shown
    formatOptions.maximumFractionDigits = maxDecimalPlaces;
  }
  // PROCESS THE VALUE
  const rawValue = amount; // for convenience...
  // CHECK SOME THINGS FIRST TO MAKE SURE WE HAVE A NUMBER
  if (!Number.isFinite(rawValue)) {
    // be careful not to reject zero 0 here -- you want 0 amounts to pass through as valid values
    // zero will not get rejected here, but null, '' will get rejected
    return null;
  }
  // numValueRaw will be Number, but may have too many digits/decimal places
  let numPattern = /[^0-9.-]+/g; // we may not need all this, as long as we are sure what we're getting in... but that will take more testing... updating this in a rush on March 3 2022 to fix a Nifco bug
  let valueIsNumber = typeof rawValue === 'number';
  let valueAsNumber = valueIsNumber ? rawValue : rawValue.replace(numPattern, ''); // string.replace() returns a new string...
  var numValueRaw = Number.parseFloat(valueAsNumber);
  if (Number.isNaN(numValueRaw)) {
    return null;
  } else {
    return numValueRaw.toLocaleString(locale, formatOptions);
  }
};

const currencySymbolsToCodes = {
  US$: 'USD',
  C$: 'CAD',
  A$: 'AUD',
  NZ$: 'NZD',
  HK$: 'HKD',
  S$: 'SGD',
  MX$: 'MXN',

  $: 'USD', // fallback for $, but technically ambiguous since multiple currencies use $
  '€': 'EUR',
  '£': 'GBP',
  '¥': 'CNY',  // NOTE: this is ambiguous between JPY and CNY... we need to decide which one to use... we have more China users, so we will default to CNY for the bare symbol
  'JP¥': 'JPY',
  'CN¥': 'CNY',
  '₩': 'KRW',
  '₹': 'INR',
  '₽': 'RUB',
  '₺': 'TRY',
  '₫': 'VND',
  '₦': 'NGN',
  R$: 'BRL',
  ZAR: 'ZAR',
  CHF: 'CHF',
  AED: 'AED',
  SAR: 'SAR',
  ILS: 'ILS',
  '₪': 'ILS',
};

function extractCurrencyAmountFromString(text, locale) {
    // pass in a text value, this returns [currency_code, amount]
    // will always return a 2-element array if it finds something, possibly [null, null] if nothing found
    // returned amount is a NUMBER if found, and parsed using LOCALE-AWARE decimal parser
    // you can override which locale to use for parsing by passing in a locale string (like 'en-US' or 'de-DE')
    // if you don't pass in a locale, it will use the browser's default locale
    // handles both symbol and 3-letter code cases for currency
    // extracts the amount even if written with commas or decimals (locale-aware)
    // what about decimal places?  let caller handle that... this will just parse the number as-is... 
    // ...but maybe we should handle that here... maybe that is needed to make parsing more robust?
    // ...but we also want to preserve the number of decimal places the user entered, so maybe not change that here

    const allCurrencyCodes = makeUnique(Object.values(currencySymbolsToCodes));
    // const isValidCode = (code) => allCurrencyCodes.includes(code);
    

    // CHECK FOR CURRENCY CODES FIRST - because they are unambiguous, unlike symbols like $
    // const currencyCodePattern = /[A-Z]{3}/;
    const amountPattern = /([\d,.]+)/g; // matches numbers with optional decimal or thousands separator (but no negative sign... is that needed???  it would be very difficult, because it might appear before the currency symbol....)
    
    for (const currCode of allCurrencyCodes) {
        if (text.includes(currCode)) {
            // we replace the code with empty string, then try to parse the number from the rest of the text (after trimming whitespace))
            const textWithoutCode = text.replaceAll(currCode, '').trim();
            let amountMatch = textWithoutCode.match(amountPattern);
            if (amountMatch) {
                const rawAmount = amountMatch[0];
                const parsedAmount = parseDecimal(rawAmount, locale);
                return isNaN(parsedAmount) ? [currCode, null] : [currCode, parsedAmount];
            }

            // no match of number... we'll return code and null for amount
            return [currCode, null];
        }
    }
    
    // STUFF BELOW USES REGEX TO FIND CURRENCY AND AMOUNT TOGETHER IN THE TEXT -- but if it doesn't find currency, it will return null...
    // that is not always what we want... so we will try a bit slower way, similar to what we do for symbols below, but should be more robust for parsing amounts without currency
    // it is common to have amounts without currency, especially when parsing from excel values or similar (especially for single-currency scenarios) so this is quite an important use-case

    // const codeAmountPattern = /([A-Z]{3})\s*([\d,.]+)/;  // matches "USD 1000", with or without space, and with commas or dots in number
    // const amountCodePattern = /([\d,.]+)\s*([A-Z]{3})/;  // matches "1000 USD", with or without space, and with commas or dots in number

    // // try 3-letter currency codes + amount
    // let match = text.match(codeAmountPattern);
    // if (match) {
    //     const rawAmount = match[2];
    //     const parsedAmount = parseDecimal(rawAmount, locale);
    //     const code = match[1];
    //     if (!isValidCode(code)) {  // gotta check that the code is valid, since we only looked for 3-letter sequences
    //         return null;
    //     }
    //     return [code, parsedAmount];
    // }

    // // try amount then code
    // match = text.match(amountCodePattern);
    // if (match) {
    //     const rawAmount = match[1];
    //     const parsedAmount = parseDecimal(rawAmount, locale);
    //     const code = match[2];
    //     if (!isValidCode(code)) {  // gotta check that the code is valid, since we only looked for 3-letter sequences
    //         return null;
    //     }
    //     return [code, parsedAmount];
    // }

    // still going?  try symbol mapping (longer symbols first)
    const sortedSymbols = Object.keys(currencySymbolsToCodes).sort((a, b) => b.length - a.length); // (will be unique) sort by length descending so we match longer symbols first (like US$ before $)
    // const isValidSymbol = (symbol) => allCurrencySymbols.includes(symbol);

    for (const symbol of sortedSymbols) {
        if (text.includes(symbol)) {
            // found a symbol in the text
            const code = currencySymbolsToCodes[symbol];  // if it was in the mapping, it should be valid

            // we replace the symbol with empty string, then try to parse the number from the rest of the text (after trimming whitespace))
            const textWithoutSymbol = text.replaceAll(symbol, '').trim();
            let amountMatch = textWithoutSymbol.match(amountPattern);
            if (amountMatch) {
                const rawAmount = amountMatch[0];
                const parsedAmount = parseDecimal(rawAmount, locale);
                return isNaN(parsedAmount) ? [code, null] : [code, parsedAmount];
            }

            // no match of number... we'll return code and null for amount
            return [code, null];
        }
    }

    // if we get here, we found no currency at all (code or symbol)
    // BUT, we might still want to try to parse a number from the text, if it has one
    // this is a fairly common use-case, especially in single-currency scenarios, so we will support it, just returning null for currency code
    let amountMatch = text.match(amountPattern);
    if (amountMatch) {
        const rawAmount = amountMatch[0];
        const parsedAmount = parseDecimal(rawAmount, locale);
        return isNaN(parsedAmount) ? [null, null] : [null, parsedAmount];
    }

    return [null, null]; // nothing found at all... so we always return a 2-element array
}

// Examples
// const examples = [
//   'Price is 100 USD',
//   'Total: €250',
//   'Amount: $50',
//   'Pay 300 GBP now',
//   'JPY 12000',
//   'Cost: A$400',
//   'Invoice 500 CHF',
//   'Payment: HK$2,000',
//   'Deposit: ₹7,500',
//   'CN¥ 999',
//   '₽ 1,000.50',
//   'Bought for R$120',
//   '₦ 5000',
//   '₪200 transfer',
// ];

// for (const text of examples) {
//   console.log(text, '->', extractCurrency(text));
// }

export { formatMoney, extractCurrencyAmountFromString };
